import type { SittingPart } from './sitting';
import type { TestModule, TestVariant } from './tests';
import type { WritingTaskMark } from './results';

/** Going back over a paper: what the server sends and how to read it. */

export interface ReviewEvidence {
  start: number;
  end: number;
  answerStart: number;
  answerEnd: number;
  quote: string;
}

export interface ReviewMark {
  questionNumber: number;
  given: string | null;
  correct: boolean;
  /** Only ever filled for staff. A candidate is never sent the key. */
  accepted: string[];
  evidence: ReviewEvidence | null;
  /** Which part's passage those offsets belong to. */
  evidencePart: number | null;
}

export interface Review {
  result: { id: string; band: number | null; score: number | null; satAt: string | null };
  candidate: { fullName: string; candidateNumber: string | null };
  showsAnswers: boolean;
  marks: ReviewMark[];
  byType: { type: string; label: string; correct: number; asked: number }[];
  answers: { questionNumber: number; value: string; flaggedForReview: boolean }[];
  test: {
    id: string;
    title: string;
    module: TestModule;
    variant: TestVariant;
    durationMs: number | null;
    audio: { url: string; filename: string; durationMs: number | null }[];
  };
  parts: SittingPart[];
  /** A Writing paper's marking and scripts; null on Listening and Reading. */
  writing: WritingReview | null;
}

export interface WritingReview {
  marks: WritingTaskMark[];
  essays: Record<string, string>;
  scriptsClearedAt: string | null;
  scriptWords: Record<number, number> | null;
  markedAt: string | null;
  releasedAt: string | null;
}

/**
 * The fewest words each task asks for.
 *
 * Printed on every paper — "Write at least 150 words", "at least 250" — and
 * the same for Academic and General Training. A script under it loses marks
 * for that alone, whatever else it does well, which makes it the one thing on
 * a Writing review a candidate can fix next time without anybody's help.
 */
export const WORD_MINIMUM: Record<1 | 2, number> = { 1: 150, 2: 250 };

export interface LengthCheck {
  words: number;
  minimum: number;
  /** How many words it fell short by; zero when it reached the minimum. */
  short: number;
}

export function lengthCheck(task: 1 | 2, words: number): LengthCheck {
  const minimum = WORD_MINIMUM[task];
  return { words, minimum, short: Math.max(0, minimum - words) };
}

export interface ReviewRecording {
  url: string;
  label: string;
  durationMs: number | null;
  /** True when this one file is the whole paper, not the part on screen. */
  wholePaper: boolean;
}

/**
 * The recording to offer beside a part.
 *
 * A paper holds either one recording per part or one for the whole paper.
 * Per part, each part plays its own — and a part that has none gets none,
 * rather than a neighbour's. One for the whole paper cannot be cut into parts,
 * because nothing records where Part 3 begins; it is offered on every part,
 * and says it is the whole recording rather than pretending to be Part 3's.
 */
export function recordingFor(
  review: Pick<Review, 'parts' | 'test'>,
  partIndex: number,
): ReviewRecording | null {
  const perPart = review.parts.some((p) => p.audio);
  if (perPart) {
    const audio = review.parts.find((p) => p.index === partIndex)?.audio;
    return audio
      ? { url: audio.url, label: `Part ${partIndex} recording`, durationMs: audio.durationMs, wholePaper: false }
      : null;
  }
  const whole = review.test.audio[0];
  return whole
    ? { url: whole.url, label: 'The whole recording', durationMs: whole.durationMs, wholePaper: true }
    : null;
}

/**
 * What a question type is called to a candidate.
 *
 * The stored names are the parser's — shouted, underscored, and written for
 * whoever reads the template. A breakdown telling somebody they are weak at
 * "SUMMARY_COMPLETION_BANK" has told them nothing.
 */
export const TYPE_LABEL: Record<string, string> = {
  NOTE_COMPLETION: 'Note completion',
  FORM_COMPLETION: 'Form completion',
  TABLE_COMPLETION: 'Table completion',
  FLOWCHART_COMPLETION: 'Flow-chart completion',
  SENTENCE_COMPLETION: 'Sentence completion',
  SUMMARY_COMPLETION: 'Summary completion',
  SUMMARY_COMPLETION_BANK: 'Summary, word bank',
  DIAGRAM_LABEL: 'Diagram labelling',
  MAP_LABEL: 'Map labelling',
  SHORT_ANSWER: 'Short answer',
  MCQ_SINGLE: 'Multiple choice',
  MCQ_MULTI: 'Multiple choice, several',
  MATCHING: 'Matching',
  MATCHING_HEADINGS: 'Matching headings',
  MATCHING_INFO: 'Matching information',
  MATCHING_FEATURES: 'Matching features',
  MATCHING_ENDINGS: 'Matching endings',
  TFNG: 'True / False / Not Given',
  YNNG: 'Yes / No / Not Given',
};
